import React, { Fragment } from "react";
import { Component } from "react";
import { Button, List } from "semantic-ui-react";
import moment from "moment";
import { ObjectLiteral } from "../../constants";
import _ from "lodash";
import PhoneNumber from "awesome-phonenumber";
import { ReactCookieProps, withCookies } from "react-cookie";
import base64 from "base-64";
import utf8 from "utf8";
import * as Sentry from "@sentry/nextjs";
import { parentDataRequiredToRegister } from "../../constants";
import ShowError from "./ShowError";
import { Props, registrationType } from "../propTypes";
import { childFormComponent } from "./types";
import { User } from "@sentry/nextjs";
import { ProgressIndicator } from "./ProgressIndicator";
import { ResetForm } from "./ResetForm";

// Confirmation Data Component
/**
 * This component is used to display a confirmation for the user to verify their information
 * or an error if there is no data.
 *
 * React Cookie is being used to set a cookie on the user's browser for 30 minutes. More information about this
 * can be found at https://www.npmjs.com/package/react-cookie/
 *
 * Utf8 and base64 for text-encoding. More information about these can be found at
 * https://www.npmjs.com/package/base-64/ and https://www.npmjs.com/package/utf8/
 *
 * moment.js is used to format the selected start date. For more information, check the documentation
 * at https://momentjs.com/docs/.
 */

type State = {
    searchContactError: string;
    showError: boolean;
    errorToShow: JSX.Element | string;
    userDataRaw: User | null;
};

class ConfirmationData extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    state = {
        searchContactError: "",
        showError: false,
        errorToShow: "",
        userDataRaw: null,
    };
    // Component Did Update Lifecycle Method
    /**
     * componentDidUpdate() runs immediately after the component updates.
     * This method does not run during the initial render.
     *
     * In this instance, we are validating the code entered by the user. If there is an error, we return a message.
     * If valid, we create a cookie for 30 minutes, so that if the user goes to the form again, they can skip this step.
     */
    componentDidUpdate(
        prevProps: Props & childFormComponent & ReactCookieProps
    ) {
        if (
            this.props.main.searchContactData &&
            this.props.main.searchContactData.status_code &&
            this.props.main.searchContactData.status_code !== 200 &&
            this.props.main.searchContactData.status_code !== 409 &&
            this.props.main.searchContactData.status_code !== 404 &&
            prevProps.main.searchContactData &&
            prevProps.main.searchContactData.status_code !==
                this.props.main.searchContactData.status_code
        ) {
            let errorText = "Error sending data. Please try again later.";
            if (
                this.props.main.searchContactData.message &&
                _.isArray(this.props.main.searchContactData.message) &&
                this.props.main.searchContactData.message[0] &&
                _.isObject(this.props.main.searchContactData.message[0])
            ) {
                errorText =
                    this.props.main.searchContactData &&
                    this.props.main.searchContactData.messages &&
                    (
                        this.props.main.searchContactData
                            .message as ObjectLiteral[]
                    )[0][
                        Object.keys(
                            this.props.main.searchContactData.message[0]
                        )[0]
                    ];
            }

            const extraErrorData = {
                status_code: this.props.main.searchContactData.status_code,
                other_validation_data: this.props.main.searchContactData,
            };

            if (this.props.main.searchContactData.origin !== "USER") {
                this.props.logError &&
                    this.props.logError(
                        errorText,
                        Sentry.Severity.Fatal,
                        extraErrorData
                    );
            }

            this.setState({
                showError: true,
                errorToShow: errorText,
            });
        }

        if (
            this.props.main.searchContactError &&
            this.props.main.searchContactError !== this.state.searchContactError
        ) {
            if (
                this.props.main.searchContactError &&
                this.props.main.searchContactError ===
                    "CONTACT_DATA_DOESNT_MATCH"
            ) {
                this.setState({
                    searchContactError: this.props.main.searchContactError,
                });
                this.props.nextStep(8);
            } else {
                let errorText = (
                    <Fragment>
                        <p className="text-dark half-here">
                            There was an error processing your request.
                        </p>
                        <p className="text-dark half-here no-margin inline-block">
                            Please contact us at{" "}
                        </p>
                        <a
                            href="mailto:digital@elevationchurch.org"
                            className="text-orange ec-primary-font font-size-20 m-x-spacer-2xs"
                        >
                            digital@elevationchurch.org
                        </a>
                        <p className="text-dark half-here no-margin inline-block">
                            {" "}
                            to report your error.
                        </p>
                    </Fragment>
                );

                this.setState({
                    showError: true,
                    searchContactError: this.props.main.searchContactError,
                    errorToShow: errorText,
                });
            }
        }

        if (
            this.props.main.searchContactData &&
            (this.props.main.searchContactData.status_code === 200 ||
                this.props.main.searchContactData.status_code === 409 ||
                this.props.main.searchContactData.status_code === 404) &&
            prevProps.main.searchContactData &&
            prevProps.main.searchContactData.status_code !==
                this.props.main.searchContactData.status_code
        ) {
            const { cookies, values } = this.props;
            this.setState({
                showError: false,
                searchContactError: "",
                errorToShow: "",
            });

            if (
                this.props.main.searchContactData.data &&
                this.props.main.searchContactData.data.records &&
                this.props.main.searchContactData.data.records.length === 1
            ) {
                let confirmationCodeCookieMaxAge = 1800; // 30 minutes
                if (cookies && this.props.registrationType) {
                    const cookieData = cookies.get(
                        "confirmation_" +
                            this.props.registrationType.toLowerCase()
                    );
                    let contentSplit: Array<any> | null = null;

                    if (cookieData) {
                        const cookieDataDecoded = base64.decode(cookieData);
                        contentSplit = cookieDataDecoded.split("+++");
                    }

                    let text = `${JSON.stringify({
                        type: "phone",
                        data: {
                            phoneNumber: values.phoneConfirmation,
                            extraData: values.phoneConfirmationFormat,
                        },
                    })}+++`;
                    // let text = `${values.phoneConfirmation}+++`;
                    if (cookieData && contentSplit && contentSplit.length > 0) {
                        text = `${contentSplit[0]}+++${JSON.stringify({
                            ...this.props.main.searchContactData.data
                                .records[0],
                            fromSalesForce: true,
                        })}`;
                    } else if (values.emailConfirmation) {
                        text = `${text}${JSON.stringify({
                            ...this.props.main.searchContactData.data
                                .records[0],
                            fromSalesForce: true,
                        })}`;
                    } else {
                        text = `${text}${JSON.stringify({
                            ...this.props.main.searchContactData.data
                                .records[0],
                            fromSalesForce: true,
                        })}`;
                    }

                    const bytes = utf8.encode(text);
                    const content = base64.encode(bytes);

                    if (this.props.saveSession) {
                        cookies.set(
                            "confirmation_" +
                                this.props.registrationType.toLowerCase(),
                            content,
                            { path: "/", maxAge: confirmationCodeCookieMaxAge }
                        );
                    }
                }

                this.props.handleChange("fromSalesForce")(null, true);
                this.props.nextStep(16);
            } else {
                this.props.nextStep(7);
            }
        }

        const { fromSalesForce } = this.props.values;

        if (
            this.props.main.userData &&
            !_.isEqual(this.props.main.userData, this.state.userDataRaw) &&
            fromSalesForce
        ) {
            const data = this.props.main.userData;

            let mobileNumber;
            let firstName;
            let lastName;
            let email;
            let birthdate;
            let mailingAddress;

            ({
                mobileNumber,
                firstName,
                lastName,
                email,
                birthdate,
                mailingAddress,
            } = data);

            if (
                !(mobileNumber && firstName && lastName && email && birthdate)
            ) {
                this.props.logError &&
                    this.props.logError(
                        "Incomplete User Data",
                        Sentry.Severity.Fatal,
                        {
                            mobileNumber,
                            firstName,
                            lastName,
                            email,
                            birthdate,
                            mailingAddress,
                        }
                    );
            }

            this.setState({
                userDataRaw: this.props.main.userData,
            });
        }
    }

    // Data To Send Function
    /**
     * dataToSend() stores the values of the user's information based on the type of confirmation used.
     */
    dataToSend = () => {
        const { values } = this.props;
        const dataToSend: ObjectLiteral = {};

        if (values.phoneConfirmation) {
            const phoneNumber = values.phoneConfirmation.replace(/\D/g, "");

            dataToSend["mobile_number"] = phoneNumber.replace(
                values.phoneConfirmationFormat.dialCode,
                ""
            );
            dataToSend["mobile_dialing_code"] =
                values.phoneConfirmationFormat.dialCode;
            dataToSend["email"] = values.email;
        }

        if (values.emailConfirmation) {
            const phoneNumber = values.phone.replace(/\D/g, "");
            dataToSend["email"] = values.emailConfirmation;
            dataToSend["mobile_dialing_code"] = values.phoneFormat.dialCode;
            dataToSend["mobile_number"] = phoneNumber.replace(
                values.phoneFormat.dialCode,
                ""
            );
        }

        return dataToSend;
    };

    getEventId = () => {
        if (
            this.props.registrationType === registrationType.eTeam &&
            this.props.eTeams.meetAndGreetRegisterData &&
            _.isArray(this.props.eTeams.meetAndGreetRegisterData.orientations)
        ) {
            const event =
                this.props.eTeams.meetAndGreetRegisterData.orientations.filter(
                    (event) => event.id === this.props.router?.query.eventid
                )[0];
            return event["id"];
        } else if (
            this.props.registrationType === registrationType.eLeader &&
            _.isArray(this.props.eGroups.leaderEventsCampusData)
        ) {
            const event = this.props.eGroups.leaderEventsCampusData.filter(
                (event) => event.id === this.props.router?.query.eventid
            )[0];
            return event["id"];
        } else if (
            this.props.registrationType === registrationType.event &&
            this.props.events.eventData
        ) {
            return this.props.events.eventData.id;
        } else {
            return "";
        }
    };

    // Save and Continue Function
    /**
     * saveAndContinue() saves the values to the state and goes to the corresponding next step.
     * If we have information from Salesforce, use that, if not, use the form information.
     */
    saveAndContinue = (e: any) => {
        e.preventDefault();

        const { values } = this.props;
        let searchAndCreate = true;

        if (this.props.disableSearchAndCreate) {
            searchAndCreate = false;
        }

        if (values.fromSalesForce) {
            this.props.nextStep(7);
        } else {
            const { values } = this.props;

            const dataToSend: ObjectLiteral = {
                ...this.dataToSend(),
                first_name: values.firstName,
                last_name: values.lastName,
                address: values.address,
                city: values.city,
                zip: values.zip,
                country: values.country,
                location: values.campus,
                campus_name: values.campusName,
                birthdate: moment(values.birthday).format("YYYY-MM-DD"),
            };

            if (this.props.registrationType === registrationType.Baptism) {
                dataToSend.interested_in_baptism = "1";

                if (values.tshirtSize) {
                    dataToSend.tshirt_size = values.tshirtSize;
                }
            }

            if (this.props.registrationType === registrationType.Salvation) {
                dataToSend.accepted_jesus = "1";
            }

            if (
                this.props.registrationType === registrationType.eGroup &&
                this.props.eGroups.eGroupData
            ) {
                dataToSend.group_id = this.props.eGroups.eGroupData.id || "";
            }

            if (parentDataRequiredToRegister(moment(values.birthday))) {
                dataToSend.is_youth = "1";
                dataToSend.parent_first_name = values.parentFirstName;
                dataToSend.parent_last_name = values.parentLastName;
                dataToSend.parent_email = values.parentEmail;
                dataToSend.parent_phone = values.parentPhone
                    ? "+" + values.parentPhone
                    : "";
            }

            if (
                this.props.registrationType === registrationType.eTeam ||
                this.props.registrationType === registrationType.eLeader ||
                this.props.registrationType === registrationType.event
            ) {
                dataToSend.event_id = this.getEventId();
            }

            if (values.country === "US" && values.state) {
                dataToSend["state"] = values.state;
            }

            this.props.actions.main.main_search_contact({
                dataToSearch: dataToSend,
                searchOrCreate: searchAndCreate,
            });
        }
    };

    // Change Details Function
    /**
     * changeDetails() allows the user to update the information stored through form inputs,
     * NOT FROM SALESFORCE.
     */
    changeDetails = (e: any) => {
        e.preventDefault();
        this.props.changeDetails && this.props.changeDetails();
    };

    renderError = () => {
        return (
            <div className="col-xs-12 max-w-500 full-width center-content">
                <List className="background-white p-t-spacer-xl p-b-spacer-lg p-x-spacer-lg ec-border-radius ec-box-shadow">
                    <List.Item>
                        <List.Content>
                            <div className="text-center">
                                {this.state.errorToShow}
                            </div>
                        </List.Content>
                    </List.Item>
                </List>
            </div>
        );
    };

    render() {
        if (this.state.showError) {
            return this.renderError();
        }

        let data: ObjectLiteral = {};
        let dataComplete = false;
        let mobileNumber;
        let firstName;
        let lastName;
        let email;
        let birthdate;
        let mailingAddress;
        let street;
        let city;
        let state;
        let zip;
        let country;
        let phoneFormat;
        let parentPhoneFormatData;
        let phone;
        let phoneConfirmationFormat;
        let phoneConfirmation;
        let emailConfirmation;
        let birthday;
        let address;
        let campusName;
        let countryName;
        let parentFirstName;
        let parentLastName;
        let parentEmail;
        let parentPhone;
        let extraFields: Array<{
            // field: string;
            value: string;
            label: string;
        }> = [];

        const { fromSalesForce } = this.props.values;

        if (_.isObject(this.props.main.userData) && fromSalesForce) {
            data = this.props.main.userData;
            ({ emailConfirmation, phoneConfirmation, phoneConfirmationFormat } =
                this.props.values);

            ({
                mobileNumber,
                firstName,
                lastName,
                email,
                birthdate,
                mailingAddress,
            } = data);

            if (
                (this.props.disableSaveAndCreate &&
                    mobileNumber &&
                    firstName &&
                    lastName) ||
                (mobileNumber && firstName && lastName && email)
            ) {
                dataComplete = true;
            }

            if (parentDataRequiredToRegister(birthdate)) {
                ({ parentFirstName, parentLastName, parentEmail, parentPhone } =
                    this.props.values);

                if (
                    !parentFirstName ||
                    !parentLastName ||
                    !parentEmail ||
                    !parentPhone
                ) {
                    dataComplete = false;
                }
            }

            if (dataComplete) {
                street = mailingAddress ? mailingAddress.street : "";
                city = mailingAddress ? mailingAddress.city : "";
                state = mailingAddress ? mailingAddress.stateCode : "";
                zip = mailingAddress ? mailingAddress.postalCode : "";
                country = mailingAddress ? mailingAddress.country : "";
            }
        } else if (this.props.values) {
            ({
                phone,
                firstName,
                lastName,
                email,
                emailConfirmation,
                birthday,
                address,
                city,
                state,
                zip,
                countryName,
                phoneFormat,
                parentPhoneFormatData,
                campusName,
                phoneConfirmation,
                phoneConfirmationFormat,
                parentFirstName,
                parentLastName,
                parentEmail,
                parentPhone,
            } = this.props.values);
            if (
                (phone || phoneConfirmation) &&
                firstName &&
                lastName &&
                (email || emailConfirmation) &&
                birthday &&
                address &&
                city &&
                ((zip && countryName && countryName === "US") ||
                    (countryName && countryName !== "US"))
            ) {
                dataComplete = true;
            }

            if (
                parentDataRequiredToRegister(moment(birthday)) &&
                (!parentFirstName ||
                    !parentLastName ||
                    !parentEmail ||
                    !parentPhone)
            ) {
                dataComplete = false;
            }

            if (phone) {
                mobileNumber = phone;
            } else if (phoneConfirmation) {
                mobileNumber = phoneConfirmation;
            }

            if (birthday) {
                birthdate = birthday;
            }

            if (address) {
                street = address;
            }

            if (countryName) {
                country = countryName;
            }
        }

        if (
            this.props.showConfirmationExtraFields &&
            this.props.showConfirmationExtraFields.length > 0
        ) {
            this.props.showConfirmationExtraFields.forEach((field) => {
                if (this.props.values[field.value]) {
                    extraFields.push({
                        value: this.props.values[field.value],
                        label: field.label,
                    });
                }
            });
        }
        if (!dataComplete) {
            if (
                this.props.renderCustomUserNotFound &&
                _.isFunction(this.props.renderCustomUserNotFound)
            ) {
                return (
                    <>
                        {this.props.renderCustomUserNotFound()}
                        <ResetForm
                            idPrefix={`${this.props.tagPrefixId}-confirm-error`}
                            cancelUrl={this.props.cancelUrl || ""}
                        />
                    </>
                );
            }
            return (
                <>
                    <ShowError />
                    <ResetForm
                        idPrefix={`${this.props.tagPrefixId}-confirm-error`}
                        cancelUrl={this.props.cancelUrl || ""}
                    />
                </>
            );
        } else {
            let phoneToShow = mobileNumber;

            // Format Phone Number
            if (mailingAddress && mailingAddress.countryCode) {
                const pn = new PhoneNumber(
                    mobileNumber,
                    mailingAddress.countryCode
                );

                if (mailingAddress.countryCode === "US") {
                    phoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                        "national"
                    )}`;
                } else {
                    phoneToShow = pn.getNumber("international");
                }
            } else if (phoneFormat && phoneFormat.countryCode) {
                const pn = new PhoneNumber(
                    mobileNumber,
                    phoneFormat.countryCode
                );

                if (phoneFormat.countryCode === "us") {
                    phoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                        "national"
                    )}`;
                } else {
                    phoneToShow = pn.getNumber("international");
                }
            } else if (
                phoneConfirmationFormat &&
                phoneConfirmationFormat.countryCode
            ) {
                const pn = new PhoneNumber(
                    mobileNumber,
                    phoneConfirmationFormat.countryCode
                );

                if (phoneConfirmationFormat.countryCode === "us") {
                    phoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                        "national"
                    )}`;
                } else {
                    phoneToShow = pn.getNumber("international");
                }
            }

            let parentPhoneToShow = parentPhone;

            if (parentPhone && parentPhoneFormatData) {
                const pn = new PhoneNumber(
                    parentPhone,
                    parentPhoneFormatData.countryCode
                );
                if (parentPhoneFormatData.countryCode.toLowerCase() === "us") {
                    parentPhoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                        "national"
                    )}`;
                } else {
                    parentPhoneToShow = pn.getNumber("international");
                }
            }

            const { fromSalesForce } = this.props.values;

            return (
                <>
                    <div className="col-xs-12 max-w-500 center-content full-width m-b-spacer-3xl">
                        <h2 className="text-dark text-left m-b-spacer-none font-size-24 text-center m-t-spacer-lg">
                            Confirm Personal Info
                        </h2>
                        <p className="text-dark half-here text-center m-b-spacer-lg">
                            Does your information look correct?
                        </p>
                        <List>
                            {campusName ? (
                                <List.Item className="m-b-spacer-md">
                                    <List.Content>
                                        <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                            Primary Campus
                                        </h5>
                                        <small>{campusName}</small>
                                    </List.Content>
                                </List.Item>
                            ) : null}
                            <List.Item className="m-b-spacer-md">
                                <List.Content>
                                    <h5 className="text-dark sec-font half-here text-dark no-bottom-margin no-top-margin">
                                        Name
                                    </h5>
                                    <small>
                                        {firstName} {lastName}
                                    </small>
                                </List.Content>
                            </List.Item>
                            <List.Item className="m-b-spacer-md">
                                <List.Content>
                                    <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                        Phone Number
                                    </h5>
                                    <small>{phoneToShow}</small>
                                </List.Content>
                            </List.Item>
                            {email ? (
                                <List.Item className="m-b-spacer-md">
                                    <List.Content>
                                        <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                            Email
                                        </h5>
                                        <small>{email}</small>
                                    </List.Content>
                                </List.Item>
                            ) : null}

                            <List.Item className="m-b-spacer-md">
                                <List.Content>
                                    <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                        Birthdate
                                    </h5>
                                    <small>
                                        {moment(birthdate).format("MM/DD/YYYY")}
                                    </small>
                                </List.Content>
                            </List.Item>

                            {extraFields.map((field, key) => (
                                <List.Item className="m-b-spacer-md" key={key}>
                                    <List.Content>
                                        <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                            {field.label}
                                        </h5>
                                        <small>{field.value}</small>
                                    </List.Content>
                                </List.Item>
                            ))}

                            <List.Item className="m-b-spacer-md">
                                <List.Content>
                                    <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                        Address
                                    </h5>
                                    <small
                                        dangerouslySetInnerHTML={{
                                            __html: `${street}${
                                                street ? "<br />" : ""
                                            } ${city}${state ? "," : ""} ${
                                                state || ""
                                            } ${zip || ""} <br /> ${country}`,
                                        }}
                                    />
                                </List.Content>
                            </List.Item>

                            {parentDataRequiredToRegister(moment(birthdate)) ? (
                                <Fragment>
                                    <List.Item className="m-b-spacer-md">
                                        <List.Content>
                                            <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                                Parent Name
                                            </h5>
                                            <small>
                                                {parentFirstName}{" "}
                                                {parentLastName}
                                            </small>
                                        </List.Content>
                                    </List.Item>
                                    <List.Item className="m-b-spacer-md">
                                        <List.Content>
                                            <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                                Parent Email
                                            </h5>
                                            <small>{parentEmail}</small>
                                        </List.Content>
                                    </List.Item>
                                    <List.Item className="m-b-spacer-md">
                                        <List.Content>
                                            <h5 className="text-dark sec-font half-here text-dark no-bottom-margin">
                                                Parent Phone
                                            </h5>
                                            <small>{parentPhoneToShow}</small>
                                        </List.Content>
                                    </List.Item>
                                </Fragment>
                            ) : null}

                            {this.props.main.isLoading ? (
                                <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop m-t-spacer-md">
                                    <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                        <div />
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    className={`full-width no-border text-white  m-t-spacer-lg p-y-spacer-md uppercase let-sp-reg ec-border-radius hover-grow ${campusName}`}
                                    onClick={this.saveAndContinue}
                                    id={`${this.props.tagPrefixId}-confirm-confirm`}
                                >
                                    Confirm
                                </Button>
                            )}
                        </List>

                        <div className="m-t-spacer-lg text-center">
                            <small className="regular">
                                <span className="text-alt-dark-grey half-here">
                                    Doesn’t seem right?
                                </span>
                                {fromSalesForce ? (
                                    <a
                                        href="https://elevation.formstack.com/forms/cc_contact"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ec-no-background no-border bold help-text m-l-spacer-sm"
                                        id={`${this.props.tagPrefixId}-confirm-contact-us`}
                                    >
                                        Contact Us
                                    </a>
                                ) : (
                                    <Button
                                        onClick={this.changeDetails}
                                        className="ec-no-background no-border bold help-text hover-grow ec-border-radius"
                                        id={`${this.props.tagPrefixId}-confirm-make-changes`}
                                    >
                                        Make changes
                                    </Button>
                                )}
                            </small>
                        </div>
                        <div className="m-t-spacer-lg">
                            <ProgressIndicator currentStep={9} />
                        </div>
                    </div>
                    <ResetForm
                        idPrefix={`${this.props.tagPrefixId}-confirm`}
                        cancelUrl={this.props.cancelUrl || ""}
                    />
                </>
            );
        }
    }
}

export default withCookies(ConfirmationData);
